import * as THREE from 'three';

/**
 * Geometric constants for a flat-top hex grid.
 *
 * Coordinate conventions (see CLAUDE.md):
 *   - grid lies on the XZ plane, elevation runs along Y
 *   - CORNERS advance clockwise in maths terms, which is counter-clockwise
 *     seen from above in Three.js — that is what makes triangle normals point
 *     up for a fan wound (centre, CORNERS[i], CORNERS[i + 1])
 */

/** Hex radius: centre to corner. */
export const HEX_SIZE = 0.5;

/**
 * World-space height of a single elevation level.
 *
 * Kept at half the hex radius. Tying it to HEX_SIZE means the slope of a
 * one-level rise — rise over the spacing between centres — stays the same
 * whatever the hexes are scaled to.
 */
export const ELEVATION_STEP = HEX_SIZE / 2;

/**
 * Corner offsets from the hex centre. Seven entries so that CORNERS[i + 1]
 * is always valid for i in 0..5.
 */
export const CORNERS: THREE.Vector3[] = [];
for (let i = 0; i <= 6; i++) {
  const angle = (-i * Math.PI) / 3;
  CORNERS.push(
    new THREE.Vector3(HEX_SIZE * Math.cos(angle), 0, HEX_SIZE * Math.sin(angle))
  );
}

/** World-space Y for an elevation. Fractional values are expected at vertices. */
export function elevationToY(elevation: number): number {
  return elevation * ELEVATION_STEP;
}

/**
 * Elevation of the sea surface. Everything below it is water; the negative
 * value on such a cell is its depth, and shapes the seabed under the surface.
 */
export const SEA_LEVEL = 0;

/** Flat blue of the sea surface. */
export const WATER_COLOR = new THREE.Color(0x2e7fc9);

/**
 * Shore sand. Used both for the beach ring on land touching water and for the
 * seabed, so the two match if the water is ever made translucent.
 */
export const SAND_COLOR = new THREE.Color(0xe0cf94);

/**
 * Biome colours, keyed by the id the server sends, in two readings.
 *
 * Filled from `/api/world/biomes` at startup — the ids are the wire format and
 * the names and colours belong to the side that owns the dictionary. A copy on
 * this side would mislabel every biome the day one is added.
 *
 * **Two colours, because a biome is a climate and not a plant.** Temperate
 * ground is a meadow with nothing growing on it and a wood with trees on it;
 * cold ground is tundra or taiga by the same rule. Which one is shown is
 * decided by the forest layer, which is the thing that actually knows.
 */
const BIOME_COLORS = new Map<number, { bare: THREE.Color; wooded: THREE.Color }>();

/** Anything the legend does not cover: loud, so it cannot be mistaken for terrain. */
const UNKNOWN_BIOME = new THREE.Color(0xff00ff);

/**
 * Biomy, na których rośnie darń — czyli te, po których renderer sypie ziarnem
 * trawy.
 *
 * Zbiór identyfikatorów, a nie pytanie do słownika przy każdym wierzchołku:
 * meszowanie chunka odwiedza ich tysiące, a to jest wtedy jedno sprawdzenie
 * w tablicy mieszającej zamiast przejścia po wpisach.
 */
const GRASSY = new Set<number>();

/**
 * Czy pole ma trawę — biom mówi, że rośnie tu darń.
 *
 * **Las też ją ma**, i to nie jest przeoczenie: pod drzewami jest runo, a pole
 * zalesione i gołe różnią się tym, co na nich stoi, a nie tym, czy cokolwiek
 * pod tym rośnie.
 */
export function grassOn(biome: number | undefined): number {
  return biome !== undefined && GRASSY.has(biome) ? 1 : 0;
}

export function setBiomePalette(
  colors: Map<number, { bare: string; wooded?: string; grass?: boolean }>
): void {
  BIOME_COLORS.clear();
  GRASSY.clear();
  for (const [id, reading] of colors) {
    if (reading.grass) GRASSY.add(id);
    const bare = new THREE.Color(reading.bare);
    BIOME_COLORS.set(id, {
      bare,
      // Water and rock have no wooded reading and never will; falling back to
      // the bare one keeps the lookup total rather than making every caller
      // ask whether this biome is the kind that can have trees.
      wooded: reading.wooded ? new THREE.Color(reading.wooded) : bare,
    });
  }
}

/**
 * Fallback palette, anchored across the height range.
 *
 * Used only for a world with no biome layer — one saved before the generator
 * had them. It is the old behaviour kept alive for exactly that case, not a
 * second way of colouring the same world.
 */
const ELEVATION_COLORS = [
  new THREE.Color(0x4a7c3f), // lowlands
  new THREE.Color(0x5f9445), // meadow
  new THREE.Color(0x86a24d), // upland grass
  new THREE.Color(0x9c8555), // dry earth
  new THREE.Color(0x8a7460), // rock
  new THREE.Color(0x9a9a9a), // bare stone
  new THREE.Color(0xe8e8e8), // snow
];

/**
 * Bare rock, shown wherever the ground is too steep to hold anything else.
 *
 * Deliberately a shade apart from the palette's own "bare stone", which marks
 * altitude rather than steepness — a grey summit and a grey cliff face are two
 * different statements and should not read as the same surface.
 */
export const ROCK_COLOR = new THREE.Color(0x827f7a);

/**
 * Where rock starts showing through and where it takes over, measured in
 * elevation levels per hex step (see centerSlope in HexSurface).
 *
 * Between the two the terrain colour and the rock are mixed, so a slope shades
 * into stone rather than switching to it at a line. Both are aesthetic dials.
 *
 * **Początek zszedł z 3 na 1,5**, koniec został. Ułamek, bo nachylenie jest tu
 * liczbą rzeczywistą — różnicą poziomów wzdłuż krawędzi wachlarza, a nie
 * całkowitym krokiem między środkami pól — więc półtora jest wartością tak samo
 * dobrą jak dwa i wypada dokładnie w pół drogi między terenem płaskim
 * a najniższym zboczem, które cokolwiek zatrzymuje.
 *
 * Kamień prześwituje więc już na łagodnych fałdach, na długo przed tym, zanim
 * teren zamknie drogę. Pas mieszania jest przez to szeroki (1,5–5), a przejście
 * bardzo łagodne.
 *
 * Czego to **nie** zmienia: pola osiągające pełną skałę. Powyżej 5 mieszanie
 * i tak jest zupełne, więc ta jedna piętnasta lądu z pomiaru na mapie testowej
 * zostaje — przesunęła się tylko granica, od której cokolwiek prześwituje.
 * (Tamten pomiar — jedna trzecia lądu w pasie mieszania — był robiony przy 3.)
 */
export const ROCK_SLOPE_MIN = 1.5;
export const ROCK_SLOPE_MAX = 5;

const smoothstep = (t: number) => t * t * (3 - 2 * t);

/** How much rock shows through at a given slope, from none to all of it. */
function rockBlend(slope: number): number {
  if (slope <= ROCK_SLOPE_MIN) return 0;

  const span = ROCK_SLOPE_MAX - ROCK_SLOPE_MIN;
  return smoothstep(Math.min(1, (slope - ROCK_SLOPE_MIN) / span));
}

/**
 * Fallback colour for an elevation, blended between the two nearest entries.
 *
 * The palette is stretched across 0..maxElevation rather than anchored at whole
 * levels, so the number of elevation levels can change without touching the
 * colours. maxElevation comes from the world, not from a constant here.
 */
function elevationColor(
  elevation: number,
  maxElevation: number
): THREE.Color {
  const highest = ELEVATION_COLORS.length - 1;
  const position = Math.max(0, Math.min(1, elevation / maxElevation)) * highest;
  const lower = Math.floor(position);
  const upper = Math.min(highest, lower + 1);

  return ELEVATION_COLORS[lower]
    .clone()
    .lerp(ELEVATION_COLORS[upper], position - lower);
}

/**
 * Colour of the ground: its biome read together with whether anything grows on
 * it, turned to rock as the slope steepens.
 *
 * Steepness wins over the biome because it is the stronger statement about the
 * surface — a forested slope steep enough is a cliff face, whatever grows at
 * the top of it. That was true of the elevation palette before and is true of
 * biomes now.
 *
 * With no biome — an older world — it falls back to the height palette, so such
 * a world still renders rather than coming out magenta.
 */
export function terrainColor(
  biome: number | undefined,
  elevation: number,
  slope: number,
  maxElevation: number,
  wooded = false
): THREE.Color {
  if (biome === undefined) {
    return elevationColor(elevation, maxElevation).lerp(ROCK_COLOR, rockBlend(slope));
  }

  const reading = BIOME_COLORS.get(biome);
  const base = reading ? (wooded ? reading.wooded : reading.bare).clone() : UNKNOWN_BIOME.clone();
  return base.lerp(ROCK_COLOR, rockBlend(slope));
}

/**
 * Colour of the sea floor.
 *
 * Sand, and nothing to do with the slope: every water cell is geometrically a
 * funnel — its centre sits at its depth while all six corners are pinned to sea
 * level — so a slope rule would turn the whole sea bed to rock, which is a
 * statement about how water cells are built and not about the ground.
 *
 * The shore itself is no longer decided here. A beach is a biome now, produced
 * by the generator from which cells actually touch the sea, rather than guessed
 * on this side from a vertex that happens to be next to water.
 */
export function seabedColor(): THREE.Color {
  return SAND_COLOR.clone();
}
